import { useEffect, useMemo } from 'react';
import { useUserSession } from '@serpro/login-cidadao-volto/hooks/useUserSession';
import { useDispatch, useSelector } from 'react-redux';

import { getUserRecommendations } from '../../../govbr.frontend.policy/src/actions/user_recom/user_recom';

/**
 * Hook para gerenciar quando buscar recomendações de usuários.
 * Implementa dependências entre estados: só busca recomendações quando
 * o estado da sessão é conhecido e o histórico está carregado.
 *
 * @returns {Object} Retorno do hook.
 * @returns {Array} [recommendations] - Lista de recomendações de usuários.
 * @returns {boolean} [loaded] - Indica se as recomendações foram carregadas.
 * @returns {boolean} [loading] - Indica se as recomendações estão sendo carregadas.
 */
export const useUserRecommendations = () => {
  const dispatch = useDispatch();

  const { items, loaded, loading, error } = useSelector(
    (state) => state.user_recom,
  );

  // Buscar estado do histórico do usuário
  const { items: history, loaded: historyLoaded } = useSelector(
    (state) => state.user_history,
  );

  // Buscar dados de serviços em destaque e mais acessados
  const {
    servicosDestaque,
    servicosMaisAcessados,
    recomUrl,
    loaded: govHomeServicesLoaded,
  } = useSelector((state) => state.gov_home_services);

  // Obter informações da sessão do usuário
  const { userSession, sessionState } = useUserSession();

  // Verificar condições para buscar recomendações
  const shouldFetchRecommendations = useMemo(() => {
    return (
      sessionState !== undefined &&
      historyLoaded &&
      govHomeServicesLoaded &&
      recomUrl
    );
  }, [sessionState, historyLoaded, govHomeServicesLoaded, recomUrl]);

  const balckList = useMemo(() => {
    return [...servicosDestaque, ...servicosMaisAcessados].map(
      (item) => item.id,
    );
  }, [servicosDestaque, servicosMaisAcessados]);

  useEffect(() => {
    if (shouldFetchRecommendations) {
      dispatch(
        getUserRecommendations(
          userSession?.analyticsId,
          recomUrl,
          history || [],
          balckList,
        ),
      );
    }
  }, [
    dispatch,
    userSession,
    shouldFetchRecommendations,
    balckList,
    history,
    recomUrl,
  ]);

  // Adiciona o parâmetro origem=recom_inst nas URLs das recomendações
  const recommendationsWithOrigem = useMemo(() => {
    if (!items || !Array.isArray(items)) return items;

    return items.map((item) => ({
      ...item,
      path: item.path
        ? `${item.path}${item.path.includes('?') ? '&' : '?'}origem=recom_inst`
        : item.path,
    }));
  }, [items]);

  return {
    recommendations: recommendationsWithOrigem,
    loaded,
    loading,
    error,
  };
};

export default useUserRecommendations;
